import {Injectable} from '@angular/core';
import {AlertController} from '@ionic/angular';
import {TranslateService} from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Device } from '@capacitor/device';
import { Share } from '@capacitor/share';


@Injectable({
    providedIn: 'root'
})
export class UtilService {

    constructor(
        private alertCtrl: AlertController,
        private translate: TranslateService
    ) {
    }

    static fallbackCopyTextToClipboard(text) {
        const textArea = document.createElement('textarea');
        textArea.value = text;

        // Avoid scrolling to bottom
        textArea.style.top = '0';
        textArea.style.left = '0';
        textArea.style.position = 'fixed';

        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            const successful = document.execCommand('copy');
            const msg = successful ? 'successful' : 'unsuccessful';
            console.log('Fallback: Copying text command was ' + msg);
        } catch (err) {
            console.error('Fallback: Oops, unable to copy', err);
        }

        document.body.removeChild(textArea);
    }

    static async copyTextToClipboard(text) {
        if (!navigator.clipboard) {
            UtilService.fallbackCopyTextToClipboard(text);
            return;
        }
        try {
            await navigator.clipboard.writeText(text);
            console.log('async: Copying to clipboard was successful!');
        } catch (e) {
            console.error('async: Could not copy text: ', e);
        }
    }

    static downloadTextFile(fileName: string, fileContent: string) {
        const element = document.createElement('a');
        element.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(fileContent));
        element.setAttribute('download', fileName);

        element.style.display = 'none';
        document.body.appendChild(element);

        element.click();

        document.body.removeChild(element);
    }

    static async downloadTextFileMobile(name: string, fileContent: string) {
        console.log("downloadTextFileMobile");
        let {folderDefinition, folderString} = await this.getAppDownloadDir();

        console.log('folderDefinition', folderDefinition, 'folderString', folderString);

        const info = await Device.getInfo();
        let filepath: string;
        const oldAndroid = info.operatingSystem === 'android' && parseInt(info.osVersion, 10) < 10;
        if (oldAndroid) {
            // create base dir (will not overwrite if already existing)
            const dir = 'Download';
            try {
                await Filesystem.mkdir({
                    path: dir,
                    directory: folderDefinition,
                    recursive: false,
                });
            } catch (e) {
                console.error('Unable to make directory', e);
            } finally {
                filepath = dir + '/' + name + '.csv';
            }
        } else {
            filepath = name + '.csv';
        }

        console.log('will download to filepath', filepath);

        try {
            const result = await Filesystem.writeFile({
                path: filepath,
                data: fileContent,
                encoding: Encoding.UTF16,
                directory: folderDefinition
            });
            console.log('File written to', result.uri);
            if (!oldAndroid) {
                await Share.share({
                    url: result.uri
                });
            }

            return [result.uri, name];
        } catch (e) {
            console.error('Unable to write to file', e);
        }

    }

    static generateId(length) {
        let result = '';
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const charactersLength = characters.length;
        for (let i = 0; i < length; i++) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
        }
        return result;
    }

    async showErrorMsg(e: any) {
        const header = await firstValueFrom(this.translate.get('services.util.error-header'));
        const closeBtn = await firstValueFrom(this.translate.get('shared.btn-close'));
        let message;
        if (typeof e === 'string') {
            message = e;
        } else if (e.error && e.error.error_description) {
            message = e.error.error_description;
        } else if (e.error && e.error.message) {
            message = e.error.message;
        } else if (e.msg) {
            message = e.msg;
        } else if (e.message) {
            message = e.message;
        }
        const alert = await this.alertCtrl.create({
            header,
            message,
            buttons: [closeBtn]
        });
        await alert.present();
    }

    async showNoConnectionAlert() {
        const header = await firstValueFrom(this.translate.get('services.util.error-connection-header'));
        const message = await firstValueFrom(this.translate.get('services.util.error-connection-message'));
        const closeBtn = await firstValueFrom(this.translate.get('shared.btn-close'));
        const alert = await this.alertCtrl.create({
            header,
            message,
            buttons: [{
                text: closeBtn,
                role: 'cancel'
            }]
        });
        await alert.present();
    }

    static async getAppDownloadDir(): Promise<{folderDefinition: Directory.External | Directory.ExternalStorage, folderString: 'Daten' | 'Download'}> {
      let folderDefinition: Directory.External | Directory.ExternalStorage;
      let folderString: 'Daten' | 'Download';
      const info = await Device.getInfo();
      if (info.operatingSystem === 'android' && parseInt(info.osVersion, 10) > 9) {
        console.log('Using "External" for new Android versions; scoped applications folder');
        folderDefinition = Directory.External;
        folderString = 'Daten';
      } else {
        console.log('Using External storage for old Android versions; resolves to Documents directory on iOS');
        folderDefinition = Directory.ExternalStorage;
        folderString = 'Download';
      }
      return {folderDefinition, folderString};
    }

}
